import { ToastrService } from 'ngx-toastr';
import { Observable, Subject, merge } from 'rxjs';
import { finalize, takeUntil, throttleTime } from 'rxjs/operators';

import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  OnInit,
  OnDestroy,
  HostListener,
  Output,
  EventEmitter,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatSliderChange } from '@angular/material/slider';

import { SnAbilityService } from '@lib/ability';
import { SnCameraPtzMovementType } from '@lib/api/entities';
import { SnAuditService, SnCamerasAPI } from '@lib/api/services';
import { SnPlayerState } from '@lib/player/common';
import { t } from '@lib/translation';
import { BASE_COLORS, COLORS } from '@lib/ui/theme';
import { SnAuditOperation } from 'libs/api/entities/src/lib/audit';
import { SnContentType } from 'libs/api/entities/src/lib/base';
import { SnDialogService } from 'libs/ui/dialog/src/lib/services/dialogs.service';

import { PtzControlsService } from '../../ptz-controls.service';
import { SnVideoPlayerComponent } from '../../video-player.component';

@Component({
  selector: 'sn-ptz-controls-component',
  templateUrl: './ptz-controls.component.html',
  styleUrls: ['./ptz-controls.component.scss'],
})
export class PtzControlsComponentComponent implements OnInit, OnDestroy {
  get mouseWheel$(): Observable<any> {
    return this._mouseWheel$.asObservable();
  }

  @Input()
  public ptzConfig: any;

  @Input()
  public local: boolean;

  @Input()
  public isExternalPtz: boolean;

  @Output()
  public readonly dragging = new EventEmitter<boolean>();

  public timer;
  public xPos;
  public yPos;
  public dragX = 0;
  public dragY = 0;
  public canvas: any;
  public centerX: number;
  public centerY: number;
  public createdLine: any;
  public linesToDraw: any[] = [];
  public presets = [];
  public totalPages = 0;
  public hasResults = 0;
  public page = 0;
  public amount: number = 0;
  public moveMultiplier: number = 5;
  public ptzOverlay: boolean;
  public isSaving: boolean = false;
  public isEditing: boolean = false;
  public loadingPtz: boolean = false;
  public noPresets: boolean = false;
  public isMoving: boolean = false;
  public isOpen: boolean = false;
  public isCrosshairEnabled = false;
  public isDragging: boolean = false;
  public togglePtz: boolean = false;
  public metaPagination: any = {};
  public readonly isMovePTZAllowed: boolean;
  public readonly canChangePresets: boolean;
  public isAreaZoomDrawing: boolean = false;
  public showCrosshair: boolean = false;
  public form: UntypedFormGroup = new UntypedFormGroup({ name: new UntypedFormControl('') });
  public movementType: string = 'relative';
  public videoRatio: number;
  public isLoadingSetting = [];
  public isLoadingArrowKeyControl: boolean = false;
  public keyTimer;
  public isHoldingArrowKey: boolean = false;
  public getSliderLabel = this._formatLabel.bind(this);
  public ptzModifier = this._ptzService.ptzMovementModifier;

  private readonly _destroy$ = new Subject<void>();
  private readonly _mouseWheel$ = new Subject<any>();

  constructor(
    @Inject('SN_PLAYER') public _player: SnVideoPlayerComponent,
    private readonly _ptzService: PtzControlsService,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _dialogService: SnDialogService,
    private readonly _toastrService: ToastrService,
    private readonly _ability: SnAbilityService,
    private readonly _camerasAPI: SnCamerasAPI,
    public elementRef: ElementRef,
    private readonly _auditService: SnAuditService,
  ) {
    this.isMovePTZAllowed = this._ability.can({ subject: 'cameras/camera', action: 'move_ptz' });
    this.canChangePresets = this._ability.can({
      subject: 'cameras/camera',
      action: 'change_ptz_presets',
    });
  }

  get isAxis(): boolean {
    return this.ptzConfig?.isAxis;
  }

  get isSony(): boolean {
    return this.ptzConfig?.isSony;
  }

  get isHanwha(): boolean {
    return this.ptzConfig?.isHanwha;
  }

  get isBosch(): boolean {
    return this.ptzConfig?.isBosch;
  }

  get isIPro(): boolean {
    return this.ptzConfig?.isIPro;
  }

  get isAvigilon(): boolean {
    return this.ptzConfig?.isAvigilon;
  }

  get isMilestone(): boolean {
    return this.ptzConfig?.isMilestone;
  }

  get isPtzControlZoomOnly(): boolean {
    return this.ptzConfig?.ptzControlZoomOnly;
  }

  get isPanasonic(): boolean {
    // IPro is Panasonic Subbrand
    return this.ptzConfig?.isPanasonic || this.ptzConfig?.isIPro;
  }

  get isExternalPtzAvigilon(): boolean {
    return (
      this.isExternalPtz && this.ptzConfig?.external?.externalType?.toLowerCase() === 'avigilon'
    );
  }

  @HostListener('window:keydown', ['$event'])
  public keyEvent(event: KeyboardEvent): void {
    if (
      !this.ptzConfig.ptzControlEnableArrows ||
      !this.fullscreen ||
      !this._player.live ||
      this._player.playerState !== 'playing' ||
      event.repeat
    ) {
      return;
    }
    const throttle =
      this.movementType === SnCameraPtzMovementType.CONTINUOUS
        ? 800
        : this.ptzConfig.isQognify
        ? 500
        : 200;
    clearInterval(this.keyTimer);
    const movement = { direction: '', amount: 0, coordinates: {} };
    const movePtz = (ptzMove: any) => {
      if (this.isExternalPtzAvigilon) {
        ptzMove.amount *= 30;
      }
      setTimeout(() => {
        if (this.isContinuousMovement() || this.ptzConfig.isQognify) {
          this._ptzService
            .dragPtz(ptzMove.coordinates, this.ptzConfig, this.movementType)
            .pipe(
              finalize(() => (this.isDragging = false)),
              takeUntil(this._destroy$),
            )
            .subscribe();
        } else {
          this._ptzService
            .movePtz(ptzMove.direction, ptzMove.amount, this.movementType)
            .pipe(takeUntil(this._destroy$))
            .subscribe();
        }
      }, 50);
      this.keyTimer = setInterval(() => {
        if (this.isContinuousMovement() || this.ptzConfig.isQognify) {
          this._ptzService
            .dragPtz(ptzMove.coordinates, this.ptzConfig, this.movementType, true)
            .pipe(
              finalize(() => (this.isDragging = false)),
              takeUntil(this._destroy$),
            )
            .subscribe();
        } else {
          this._ptzService
            .movePtz(ptzMove.direction, ptzMove.amount, this.movementType)
            .pipe(takeUntil(this._destroy$))
            .subscribe();
        }
      }, throttle);
    };
    switch (event.key) {
      case '-':
        this.zoomOut();
        break;
      case '=':
      case '+':
        this.zoomIn();
        break;
      case 'ArrowUp':
        this.isHoldingArrowKey = true;
        movement.direction = 'y';
        movement.amount = this.ptzConfig.ptzControlInvertY ? -0.01 : 0.01;
        movement.coordinates = { x: 0, y: movement.amount, z: 0 };
        movePtz(movement);
        break;
      case 'ArrowDown':
        this.isHoldingArrowKey = true;
        movement.direction = 'y';
        movement.amount = this.ptzConfig.ptzControlInvertY ? 0.01 : -0.01;
        movement.coordinates = { x: 0, y: movement.amount, z: 0 };
        movePtz(movement);
        break;
      case 'ArrowRight':
        this.isHoldingArrowKey = true;
        movement.direction = 'x';
        movement.amount = this.ptzConfig.ptzControlInvertX ? -0.01 : 0.01;
        movement.coordinates = { x: movement.amount, y: 0, z: 0 };
        movePtz(movement);
        break;
      case 'ArrowLeft':
        this.isHoldingArrowKey = true;
        movement.direction = 'x';
        movement.amount = this.ptzConfig.ptzControlInvertX ? 0.01 : -0.01;
        movement.coordinates = { x: movement.amount, y: 0, z: 0 };
        movePtz(movement);
        break;
    }
  }

  @HostListener('window:keyup', ['$event'])
  public onArrowKeyUp(): void {
    if (this.isContinuousMovement() && this.isHoldingArrowKey) {
      this._ptzService
        .dragPtz({ x: 0, y: 0, z: 0 }, this.ptzConfig, 'stop')
        .pipe(
          finalize(() => (this.isDragging = false)),
          takeUntil(this._destroy$),
        )
        .subscribe();
    }
    this._ptzService.ptzMovement(false);
    clearInterval(this.keyTimer);
    this.isHoldingArrowKey = false;
  }

  public ngOnInit(): void {
    this._player.ptzOn$.pipe(takeUntil(this._destroy$)).subscribe((value) => {
      this.togglePtz = value;
    });

    if (this.ptzConfig) {
      this.ptzConfig.isExternalPtz = this.isExternalPtz;
      this._ptzService.setConfig(this.ptzConfig, this.local);
      this.movementType = this.ptzConfig.movementType;
    }

    this.mouseWheel$.pipe(throttleTime(300), takeUntil(this._destroy$)).subscribe((event) => {
      this._handlePtzScroll(event);
    });

    merge(
      this._player.fullscreen$,
      this._player.play$,
      this._player.loaded$,
      this._player.state$,
      this._player.connected$,
    )
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        if (this.fullscreen && this._player.live && this._player.playerState === 'playing') {
          setTimeout(() => {
            this._togglePtzOverlay(true);
          }, 500);
        } else {
          this._togglePtzOverlay(false);
        }
      });

    this._ptzService.ptzMoved$
      .pipe(throttleTime(60000), takeUntil(this._destroy$))
      .subscribe((id) => {
        if (this.ptzConfig.id === id) {
          this._auditMove();
        }
      });
  }

  get fullscreen(): any {
    return this._player.fullscreen;
  }

  get playerState(): any {
    return this._player.playerState;
  }

  get live(): any {
    return this._player.live;
  }

  public ngOnDestroy(): void {
    clearInterval(this.timer);
    this._destroy$.next();
    this._destroy$.complete();
  }

  public openPresetsMenu(): void {
    this._ptzService.openPresetsMenu();
  }

  public zoomIn(): void {
    if (!this.togglePtz) {
      return;
    }
    this.amount = +0.01 * this.moveMultiplier;
    this.ptzZoomControl('ZOOMIN');
  }

  public zoomOut(timesToExecute?: number): void {
    if (!this.togglePtz) {
      return;
    }
    this.amount = -0.01 * this.moveMultiplier;
    if (timesToExecute && this.isSony) {
      for (let timesRan = 0; timesRan < timesToExecute; timesRan++) {
        this.ptzZoomControl();
      }
    } else if (timesToExecute && (this.isHanwha || this.isBosch || this.isExternalPtzAvigilon)) {
      let counter = 0;
      const loop = () => {
        setTimeout(() => {
          this.ptzZoomControl();
          counter++;
          if (counter < timesToExecute) {
            loop();
          }
        }, 600);
      };
      loop();
    } else {
      this.ptzZoomControl('ZOOMOUT');
    }
  }

  public ptzZoomControl(zoom?: string): void {
    let movementType = 'relative';
    let amount =
      this.isSony || this.isHanwha || this.isMilestone
        ? this.amount * 2000
        : this.isExternalPtzAvigilon
        ? this.amount * 6
        : this.amount;
    if (this.ptzConfig.isQognify) {
      this._ptzService
        .qognifyZoom(this.ptzConfig, zoom)
        .pipe(takeUntil(this._destroy$))
        .subscribe();
    } else {
      if (this.isPanasonic) {
        amount = amount * 0.01;
        if (this.isContinuousMovement()) {
          movementType = 'continuous';
          amount = this.amount > 0 ? 1 : -1;
        }
      }
      this._ptzService
        .movePtz('z', amount, movementType)
        .pipe(takeUntil(this._destroy$))
        .subscribe();
    }
  }

  public ptzToggle(): void {
    this.togglePtz = !this.togglePtz;
    this._player.setPTZ(this.togglePtz);
  }

  public allowPTZ(): boolean {
    return (
      this._player.fullscreen &&
      this.ptzConfig?.ptzMoveUrls &&
      this._player.live &&
      this._player.playerState === 'playing' &&
      this.isMovePTZAllowed
    );
  }

  public isContinuousMovement(): boolean {
    return this.movementType === 'continuous';
  }

  public showDragControls(): boolean {
    return (
      this.live &&
      this.ptzConfig &&
      !this.isPtzControlZoomOnly &&
      this.playerState === 'playing' &&
      this.isMovePTZAllowed
    );
  }

  public handleRightClicks(event: MouseEvent): void {
    if (!this.togglePtz) {
      return;
    }
    if (
      this.isAxis ||
      this.isSony ||
      this.isHanwha ||
      this.isBosch ||
      this.isIPro ||
      this.isExternalPtzAvigilon
    ) {
      event.preventDefault();
    }
  }

  public showCanvas(): void {
    if (this.isAreaZoomDrawing || this._player.playerState !== SnPlayerState.Playing) {
      return;
    }
    this.showCrosshair = true;
    this._changeDetectorRef.detectChanges();
  }

  public hideCanvas(): void {
    this.showCrosshair = false;
    this._changeDetectorRef.detectChanges();
  }

  public moveToHome(): void {
    this._ptzService.ptzHomeMove().pipe(takeUntil(this._destroy$)).subscribe();
  }

  public startAreaZoomDrawing(event: any): void {
    if (!this.togglePtz) {
      return;
    }
    if (
      (!this.isAxis &&
        !this.isSony &&
        !this.isHanwha &&
        !this.isBosch &&
        !this.isIPro &&
        !this.isExternalPtzAvigilon) ||
      this._player.playerState !== SnPlayerState.Playing
    ) {
      return;
    }

    if (event.button === 2 && event.type === 'mousedown' && event.currentTarget.id === 'ptzOverlay') {
      this.isAreaZoomDrawing = true;
      this._player.isAreaZoomDrawing = this.isAreaZoomDrawing;
      this._ptzService.ptzMovement(true);
      const svg = this.elementRef.nativeElement.querySelector('#areaZoomSvg');
      const overlay: HTMLElement = this.elementRef.nativeElement.querySelector('#ptzOverlay');
      svg.style.display = 'block';
      const svgPoint = (elem, x, y) => {
        const p = svg.createSVGPoint();
        p.x = x;
        p.y = y;
        return p.matrixTransform(elem.getScreenCTM().inverse());
      };
      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      rect.setAttributeNS(null, 'fill', 'none');
      rect.setAttributeNS(null, 'stroke', BASE_COLORS['accent-400']);
      rect.setAttributeNS(null, 'stroke-width', '5');
      rect.setAttributeNS(null, 'stroke-dasharray', '10,10');
      const start = svgPoint(svg, event.clientX, event.clientY);

      const drawRect = (e) => {
        const p = svgPoint(svg, e.clientX, e.clientY);
        const w = Math.abs(p.x - start.x);
        const h = Math.abs(p.y - start.y);
        if (p.x > start.x) {
          p.x = start.x;
        }

        if (p.y > start.y) {
          p.y = start.y;
        }

        rect.setAttributeNS(null, 'x', p.x);
        rect.setAttributeNS(null, 'y', p.y);
        rect.setAttributeNS(null, 'width', w.toString());
        rect.setAttributeNS(null, 'height', h.toString());
        svg.appendChild(rect);
      };

      const getRectCenter = () => {
        const bbox = rect.getBBox();
        const ctm = rect.getCTM();
        const cx = Math.floor(bbox.x + bbox.width / 2);
        const cy = Math.floor(bbox.y + bbox.height / 2);
        const point: SVGPoint = svg.createSVGPoint();
        point.x = cx;
        point.y = cy;
        return point.matrixTransform(ctm);
      };

      const clearRect = (e) => {
        if (e.code?.toLowerCase() === 'space' || e.code?.toLowerCase() === 'backspace') {
          svg?.removeChild(rect);
          svg.removeEventListener('mousemove', drawRect);
          svg.removeEventListener('mouseup', endDraw);
          document.removeEventListener('keydown', clearRect);
          svg.style.display = 'none';
          this.isAreaZoomDrawing = false;
          this._player.isAreaZoomDrawing = this.isAreaZoomDrawing;
          this._ptzService.ptzMovement(false);
        }
      };

      const endDraw = () => {
        svg.removeEventListener('mousemove', drawRect);
        svg.removeEventListener('mouseup', endDraw);
        document.removeEventListener('keydown', clearRect);
        const box = rect.getBoundingClientRect();
        const overlayRect = overlay.getBoundingClientRect();
        if (!box.width || !box.height) {
          this.isAreaZoomDrawing = false;
          this._player.isAreaZoomDrawing = this.isAreaZoomDrawing;
          this._ptzService.ptzMovement(false);
          svg.style.display = 'none';
          return;
        }
        const offsetX = event.clientX - box.left;
        const offsetY = event.clientY - box.top;
        const rectCenter = getRectCenter();
        const isReversed = offsetX > 0 && offsetY > 0;
        let zoom;
        if (isReversed) {
          zoom = Math.floor((overlay.offsetWidth / box.width) * 10);
          if (zoom >= 100) {
            zoom /= 10;
          }
        } else {
          zoom = Math.floor((overlay.offsetWidth / box.width) * 100);
        }
        const resolutionWidth = this.isBosch
          ? this._player.streamResolution.width
          : overlay.offsetWidth;
        const resolutionHeight = this.isBosch
          ? this._player.streamResolution.height
          : overlay.offsetHeight;
        const resX = this.isSony ? box.width : resolutionWidth;
        const resY = this.isSony ? box.height : resolutionHeight;
        const overlayCenterX = overlay.offsetLeft + overlay.offsetWidth / 2;
        const overlayCenterY = overlay.offsetTop + overlay.offsetHeight / 2;
        const clientX = event.clientX;
        const clientY = event.clientY;
        const fromX = clientX - overlayCenterX;
        const fromY = clientY - overlayCenterY;
        const x = this.isBosch
          ? this._getScreenPercetage(box.left - overlayRect.left, overlayRect.width)
          : this.isSony
          ? fromX / this.videoRatio
          : this.isHanwha
          ? box.left
          : rectCenter.x;
        const y = this.isBosch
          ? this._getScreenPercetage(box.top - overlayRect.top, overlayRect.height)
          : this.isSony
          ? fromY / this.videoRatio
          : this.isHanwha
          ? box.top
          : rectCenter.y;
        const xEnd = this.isBosch
          ? this._getScreenPercetage(box.right - overlayRect.left, overlayRect.width)
          : box.right;
        const yEnd = this.isBosch
          ? this._getScreenPercetage(box.bottom - overlayRect.top, overlayRect.height)
          : box.bottom;
        if (isReversed && (this.isSony || this.isExternalPtzAvigilon)) {
          this._ptzService
            .areaZoom(fromX, fromY, null, 0, 0, this._player.local, this.ptzConfig, null, null, box)
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
              this.zoomOut(10);
            });
        } else if (isReversed && (this.isHanwha || this.isBosch)) {
          const boxCenterX = box.left + box.width / 2;
          const boxCenterY = box.top + box.height / 2;
          const resolutionWidth = this.isBosch
            ? this._player.streamResolution.width
            : event.currentTarget.clientWidth;
          const resolutionHeight = this.isBosch
            ? this._player.streamResolution.height
            : event.currentTarget.clientHeight;
          this._ptzService
            .centerTo(
              this.isBosch
                ? this._getScreenPercetage(boxCenterX - overlayRect.left, overlayRect.width)
                : event.offsetX,
              this.isBosch
                ? this._getScreenPercetage(boxCenterY - overlayRect.top, overlayRect.height)
                : event.offsetY,
              resolutionWidth,
              resolutionHeight,
            )
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
              this.zoomOut(10);
            });
        } else if (this.isIPro && isReversed) {
          // iPro cameras don't support the 'z' parameter in areaZoom, so use center + zoom-out
          // Use overlay-relative coordinates for center point
          const centerX = (box.left - overlayRect.left + box.width / 2) / this.videoRatio;
          const centerY = (box.top - overlayRect.top + box.height / 2) / this.videoRatio;

          this._ptzService
            .centerTo(
              centerX,
              centerY,
              this._player.streamResolution.width,
              this._player.streamResolution.height,
            )
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
              this.zoomOut();
            });
        } else if (this.isIPro) {
          // Use overlay-relative coordinates
          const fromX = (box.left - overlayRect.left) / this.videoRatio;
          const fromY = (box.top - overlayRect.top) / this.videoRatio;
          const endX = (box.right - overlayRect.left) / this.videoRatio;
          const endY = (box.bottom - overlayRect.top) / this.videoRatio;

          this._ptzService
            .areaZoom(
              fromX,
              fromY,
              zoom,
              this._player.streamResolution?.width,
              this._player.streamResolution?.height,
              this._player.local,
              this.ptzConfig,
              endX,
              endY,
              box,
            )
            .pipe(takeUntil(this._destroy$))
            .subscribe();
        } else {
          this._ptzService
            .areaZoom(x, y, zoom, resX, resY, this._player.local, this.ptzConfig, xEnd, yEnd, box)
            .pipe(takeUntil(this._destroy$))
            .subscribe();
        }
        svg.removeChild(rect);
        svg.style.display = 'none';
        this.isAreaZoomDrawing = false;
        this._player.isAreaZoomDrawing = this.isAreaZoomDrawing;
        this._ptzService.ptzMovement(false);
      };

      svg.addEventListener('mousemove', drawRect);
      svg.addEventListener('mouseup', endDraw);
      document.addEventListener('keydown', clearRect);
    }
  }

  public onScrollZoom(event: any): void {
    if (!this.togglePtz) {
      return;
    }
    if (
      !this.ptzConfig ||
      !this._player.fullscreen ||
      this._player.isClipping ||
      this._player.playerState !== SnPlayerState.Playing
    ) {
      return;
    }

    this._mouseWheel$.next(event);
  }

  public centerOnClick(event: any): void {
    if (!this.togglePtz) {
      return;
    }
    if (
      !this.ptzConfig.vendorApis.cloud.ptz.ptzCenter ||
      this.isCrosshairEnabled ||
      this._player.playerState !== SnPlayerState.Playing
    ) {
      return;
    }

    if (event.button === 0 && event.type === 'click' && event.currentTarget.id === 'ptzOverlay') {
      const d = document.createElement('div');
      d.className = 'clickEffect';
      d.style.top = `${event.clientY}px`;
      d.style.left = `${event.clientX}px`;
      const overlay: HTMLElement = this.elementRef.nativeElement.querySelector('#ptzOverlay');
      overlay.appendChild(d);
      d.addEventListener('animationend', () => {
        d.parentElement.removeChild(d);
      });
      if (this.isSony) {
        const overlayCenterX = overlay.offsetLeft + overlay.offsetWidth / 2;
        const overlayCenterY = overlay.offsetTop + overlay.offsetHeight / 2;
        const clientX = event.clientX;
        const clientY = event.clientY;
        const fromX = (clientX - overlayCenterX) / this.videoRatio;
        const fromY = (clientY - overlayCenterY) / this.videoRatio;
        this._ptzService
          .areaZoom(fromX, fromY, null, 0, 0, this._player.local, this.ptzConfig)
          .pipe(takeUntil(this._destroy$))
          .subscribe();
      } else {
        const overlayRect = overlay.getBoundingClientRect();
        const x = this.isBosch
          ? this._getScreenPercetage(event.clientX - overlayRect.left, overlayRect.width)
          : event.offsetX;
        const y = this.isBosch
          ? this._getScreenPercetage(event.clientY - overlayRect.top, overlayRect.height)
          : event.offsetY;

        if (this.isIPro) {
          const fromX = (event.clientX - overlayRect.left) / this.videoRatio;
          const fromY = (event.clientY - overlayRect.top) / this.videoRatio;

          this._ptzService
            .centerTo(
              fromX,
              fromY,
              this._player.streamResolution.width,
              this._player.streamResolution.height,
            )
            .pipe(takeUntil(this._destroy$))
            .subscribe();
        } else {
          const resolutionWidth = this.isBosch
            ? this._player.streamResolution.width
            : event.currentTarget.clientWidth;
          const resolutionHeight = this.isBosch
            ? this._player.streamResolution.height
            : event.currentTarget.clientHeight;
          this._ptzService
            .centerTo(x, y, resolutionWidth, resolutionHeight)
            .pipe(takeUntil(this._destroy$))
            .subscribe();
        }
      }
    }
  }

  public startDrawing(event: any): void {
    if (!this.togglePtz || this.ptzConfig?.ptzControlEnableArrows) {
      return;
    }
    if (event.button !== 0 || this._player.playerState !== SnPlayerState.Playing) {
      return;
    }
    clearInterval(this.timer);
    this.isCrosshairEnabled = true;
    this.isDragging = true;
    this.dragging.next(true);
    this._ptzService.ptzMovement(true);
    this._changeDetectorRef.detectChanges();
    const borderWidth = 3;
    const throttle =
      this.movementType === SnCameraPtzMovementType.CONTINUOUS
        ? 800
        : this.ptzConfig.isQognify
        ? 500
        : 200;

    this.xPos = 0;
    this.yPos = 0;
    this.centerX =
      parseInt(this.canvas.offsetLeft, 10) + (parseInt(this.canvas.width, 10) + borderWidth) / 2;
    this.centerY =
      parseInt(this.canvas.offsetTop, 10) + (parseInt(this.canvas.height, 10) + borderWidth) / 2;
    this.createdLine = {
      type: 'line',
      x: this.centerX,
      y: this.centerY,
      w: 0,
      h: 0,
    };

    if (this.ptzConfig.isQognify) {
      setTimeout(() => {
        const x = this.xPos;
        const y = this.yPos;
        this._ptzService
          .dragPtz(
            {
              x,
              y,
              z: 0,
            },
            this.ptzConfig,
            this.movementType,
          )
          .pipe(
            finalize(() => (this.isDragging = false)),
            takeUntil(this._destroy$),
          )
          .subscribe();
        this.dragX = x;
        this.dragY = y;
      }, 50);
    } else {
      setTimeout(() => {
        if (this.xPos === 0 && this.yPos === 0 && this.isAxis) {
          return;
        }
        const x = this.isSony ? this.xPos * 1000 : this.xPos;
        const y = this.isSony ? this.yPos * 1000 : this.yPos;
        this._ptzService
          .dragPtz(
            {
              x: this.ptzConfig.ptzControlInvertX ? x * -1 : x,
              y: this.ptzConfig.ptzControlInvertY ? y * -1 : y,
              z: 0,
            },
            this.ptzConfig,
            this.movementType,
          )
          .pipe(
            finalize(() => (this.isDragging = false)),
            takeUntil(this._destroy$),
          )
          .subscribe();
        this.dragX = x;
        this.dragY = y;
      }, 50);
    }

    this.timer = setInterval(() => {
      const avigilonPosX = this.xPos * 10;
      const avigilonPosY = this.yPos * 10;
      const avigilonMoveX =
        avigilonPosX < 0 ? Math.max(-1, avigilonPosX) : Math.min(1, avigilonPosX);
      const avigilonMoveY =
        avigilonPosY < 0 ? Math.max(-1, avigilonPosY) : Math.min(1, avigilonPosY);
      const x = this.isSony
        ? this.xPos * 1000
        : this.isExternalPtzAvigilon
        ? avigilonMoveX
        : this.xPos;
      const y = this.isSony
        ? this.yPos * 1000
        : this.isExternalPtzAvigilon
        ? avigilonMoveY
        : this.yPos;

      if (x === this.dragX && y === this.dragY && this.isAxis) {
        return;
      }
      this._ptzService
        .dragPtz(
          {
            x: this.ptzConfig.ptzControlInvertX ? x * -1 : x,
            y: this.ptzConfig.ptzControlInvertY ? y * -1 : y,
            z: 0,
          },
          this.ptzConfig,
          this.movementType,
        )
        .pipe(
          finalize(() => (this.isDragging = false)),
          takeUntil(this._destroy$),
        )
        .subscribe();
      this.dragX = x;
      this.dragY = y;
    }, throttle);
    this.linesToDraw.push(this.createdLine);
  }

  public onContinuousMovementSpeedChange(event: MatSliderChange): void {
    this._ptzService.ptzMovementModifier = event.value;
  }

  public keepDrawing(event: any): void {
    if (!this.togglePtz) {
      return;
    }
    if (this.createdLine && this.isCrosshairEnabled) {
      this.isDragging = true;
      this.createdLine.w = event.offsetX - this.createdLine.x;
      this.createdLine.h = event.offsetY - this.createdLine.y;

      this._updatePositions(event);
      this.linesToDraw.push(this.createdLine);
    }
  }

  public stopDrawing(): void {
    if (!this.togglePtz) {
      return;
    }
    if (this.movementType === SnCameraPtzMovementType.CONTINUOUS) {
      this._ptzService
        .dragPtz({ x: 0, y: 0, z: 0 }, this.ptzConfig, 'stop')
        .pipe(
          finalize(() => (this.isDragging = false)),
          takeUntil(this._destroy$),
        )
        .subscribe();
    }
    this.isDragging = false;
    this.dragging.next(false);
    this._ptzService.ptzMovement(false);
    this.createdLine = null;
    this.linesToDraw = [];
    clearInterval(this.timer);
    this._genCrosshairContext();
    setTimeout(() => {
      this.isCrosshairEnabled = false;
    }, 200);
  }

  public toggleAutotrack(on: boolean): void {
    this._ptzService.togglePtzAutotrack(on).pipe(takeUntil(this._destroy$)).subscribe();
  }

  public ptzFocusSet(): void {
    this._ptzService.ptzFocusSet().pipe(takeUntil(this._destroy$)).subscribe();
  }

  public ptzBacklightSet(): void {
    this._ptzService.ptzBacklightSet().pipe(takeUntil(this._destroy$)).subscribe();
  }

  public ptzBacklightRemove(): void {
    this._ptzService.ptzBacklightRemove().pipe(takeUntil(this._destroy$)).subscribe();
  }

  public ptzHomeSet(): void {
    this._dialogService
      .confirm({
        message: t('cameras.ptz.confirmSetCurrentPositionAsHome'),
        confirmButton: {
          text: t('common.confirm'),
          color: 'accent',
        },
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.setHome();
      });
  }

  public setHome(): void {
    this._ptzService
      .ptzHomeSet()
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          this._toastrService.success(t('cameras.ptz.cameraPositionSaved'));
        },
        error: () => {
          this._toastrService.error(t('cameras.ptz.unableToSaveCameraPosition'));
        },
      });
  }

  public onControlSettingChange(event: MatSlideToggleChange, setting: string): void {
    this.isLoadingSetting.push(setting);
    const index = this.isLoadingSetting.indexOf(setting);
    this._camerasAPI
      .updateCamera(this.ptzConfig.id, {
        [setting]: event.checked,
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (camera) => {
          this.isLoadingSetting.splice(index, 1);
          this.ptzConfig[setting] = camera[setting];
        },
        error: () => {
          this.isLoadingSetting.splice(index, 1);
        },
      });
  }

  public toggleArrowKeys(): void {
    this.isLoadingArrowKeyControl = true;
    this._camerasAPI
      .updateCamera(this.ptzConfig.id, {
        ptzControlEnableArrows: !this.ptzConfig?.ptzControlEnableArrows,
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (camera) => {
          this.isLoadingArrowKeyControl = false;
          this.ptzConfig.ptzControlEnableArrows = camera.ptzControlEnableArrows;
        },
        error: () => {
          this.isLoadingArrowKeyControl = false;
        },
      });
  }

  private _handlePtzScroll(event: any): void {
    if (!this.togglePtz) {
      return;
    }
    const wheelDirection = event.deltaY < 0 ? 'up' : 'down';
    if (this.isAxis) {
      const x = event.target.clientWidth / 2;
      const y = event.target.clientHeight / 2;
      const resX = event.target.clientWidth;
      const resY = event.target.clientHeight;
      const zoom = wheelDirection === 'up' ? 200 : 50;
      this._ptzService
        .areaZoom(x, y, zoom, resX, resY, this._player.local, this.ptzConfig)
        .pipe(takeUntil(this._destroy$))
        .subscribe();
    } else {
      if (wheelDirection === 'up') {
        this.zoomIn();
      } else {
        this.zoomOut();
      }
    }
  }

  private _togglePtzOverlay(on: boolean): void {
    if (!this.ptzConfig) {
      return;
    }
    this.ptzOverlay = on;
    this._changeDetectorRef.detectChanges();
    if (
      this.ptzOverlay &&
      this._player.fullscreen &&
      this._player.playerState === SnPlayerState.Playing
    ) {
      const element = this.elementRef.nativeElement;
      const overlay = element.querySelector('#ptzOverlay');
      const video = this._player.video;
      const videoRatio = video.videoHeight / video.videoWidth;
      let clientRatio = video.clientHeight / video.clientWidth;
      const parent = this._player.parent;
      if (parent) {
        clientRatio = parent.clientHeight / parent.clientWidth;
      }
      if (videoRatio > clientRatio && overlay) {
        this.videoRatio = video.clientHeight / video.videoHeight;
        const newVideoWidth = video.videoWidth * this.videoRatio;
        overlay.style.width = `${newVideoWidth}px`;
        overlay.style.height = `${video.clientHeight}px`;
        overlay.style.left = `calc(50% - ${newVideoWidth / 2}px)`;
        overlay.style.top = '0px';
        overlay.style.display = 'block';
      } else {
        this.videoRatio = video.clientWidth / video.videoWidth;
        const newVideoHeight = video.videoHeight * this.videoRatio;
        if (overlay) {
          overlay.style.height = `${newVideoHeight}px`;
          overlay.style.width = `${video.clientWidth}px`;
          overlay.style.top = `calc(50% - ${newVideoHeight / 2}px)`;
          overlay.style.left = '0px';
          overlay.style.display = 'block';
        }
      }
      this.canvas = this.elementRef.nativeElement.querySelector('#ptzCrosshair');
      if (this.canvas) {
        this.centerX = parseInt(this.canvas.offsetLeft, 10) + this.canvas.width / 2;
        this.centerY = parseInt(this.canvas.offsetTop, 10) + this.canvas.height / 2;
        this._genCrosshairContext();
        this.hideCanvas();
      }
    } else {
      const element = this.elementRef.nativeElement;
      const overlay = element.querySelector('#ptzOverlay');
      if (overlay) {
        overlay.style.display = 'none';
      }
    }
  }

  private _updatePositions(event: any): void {
    this.canvas.getContext('2d').clearRect(0, 0, this.canvas.width, this.canvas.height);
    const svg = this.elementRef.nativeElement.querySelector('.shapeCanvas');
    const rect = svg.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;
    const intervalX = offsetX - this.centerX;
    const intervalY = this.centerY - offsetY;
    this.xPos = intervalX / 7500;
    this.yPos = intervalY / 7500;
  }

  private _genCrosshairContext(): void {
    if (!this.canvas) {
      return;
    }
    const context = this.canvas.getContext('2d');
    let x = this.canvas.width / 2;
    let y = this.canvas.height / 2;
    x = Math.floor(x);
    y = Math.floor(y);
    context.lineWidth = 3;
    context.moveTo(x, y - 7);
    context.lineTo(x, y + 7);
    context.moveTo(x - 7, y);
    context.lineTo(x + 7, y);
    context.strokeStyle = COLORS['accent-600'];
    context.stroke();
  }

  private _auditMove(): void {
    const payload: any = {
      object: {
        id: this.ptzConfig.id,
        name: this.ptzConfig.cameraName,
        contentType: SnContentType.Camera,
      },
      operation: SnAuditOperation.MovePtz,
    };
    this._auditService
      .createAudit([payload])
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {});
  }

  private _getScreenPercetage(coordinate: number, dimension: number): number {
    return Math.floor((coordinate / dimension) * 100 * 100);
  }

  private _formatLabel(value: number): any {
    return value;
  }
}
